/**
 * The track picker (SEO_BRIEF_04 item 7a): which of a video's subtitle tracks to take out.
 *
 * `pickerModel` is the decision and is pure, so it is tested without a DOM: whether to ask at
 * all, and what each row says. `mountTrackPicker` draws it from the `template[data-track-picker]`
 * the tool page carries, so every word on screen is in the template and the rows only add data:
 * a language, a track's own name, a format.
 */

import type { ContainerTrackInfo } from '../../../../../src/core/protocol.ts';
import { qs } from './dom.ts';

export interface PickerRow {
  readonly track: ContainerTrackInfo;
  /** "English", "English — SDH", or the track's own name, or "Track 3". */
  readonly name: string;
  /** The format as a person would say it: "SRT", "ASS", "PGS (Blu-ray)". */
  readonly format: string;
  readonly kind: ContainerTrackInfo['kind'];
}

export interface PickerModel {
  /** Every subtitle track, text tracks first, then image, then the rest; file order within. */
  readonly rows: readonly PickerRow[];
  /**
   * The track to take without asking, or null to ask. Asked whenever there is a choice: more
   * than one track of any kind. One track is taken straight away, whatever it is, and an image
   * track then goes where a picked one would (recognition, or the honest message).
   */
  readonly auto: ContainerTrackInfo | null;
  readonly textCount: number;
  readonly imageCount: number;
}

/** ISO 639-2 (what Matroska and MP4 store) to the two-letter code `Intl.DisplayNames` takes. */
const ISO_639_2: Readonly<Record<string, string>> = {
  ara: 'ar',
  bul: 'bg',
  chi: 'zh',
  zho: 'zh',
  cze: 'cs',
  ces: 'cs',
  dan: 'da',
  dut: 'nl',
  nld: 'nl',
  eng: 'en',
  est: 'et',
  fin: 'fi',
  fre: 'fr',
  fra: 'fr',
  ger: 'de',
  deu: 'de',
  gre: 'el',
  ell: 'el',
  heb: 'he',
  hin: 'hi',
  hrv: 'hr',
  hun: 'hu',
  ice: 'is',
  isl: 'is',
  ind: 'id',
  ita: 'it',
  jpn: 'ja',
  kor: 'ko',
  lav: 'lv',
  lit: 'lt',
  may: 'ms',
  msa: 'ms',
  nor: 'no',
  nob: 'nb',
  per: 'fa',
  fas: 'fa',
  pol: 'pl',
  por: 'pt',
  rum: 'ro',
  ron: 'ro',
  rus: 'ru',
  slo: 'sk',
  slk: 'sk',
  slv: 'sl',
  spa: 'es',
  srp: 'sr',
  swe: 'sv',
  tha: 'th',
  tur: 'tr',
  ukr: 'uk',
  vie: 'vi',
  tam: 'ta',
  tel: 'te',
  ben: 'bn',
  fil: 'fil',
  tgl: 'tl',
};

/** "English" for `eng` or `en`, the code itself when no name is known, null for none. */
export function languageName(code: string | null, locale = 'en'): string | null {
  if (code === null) return null;
  const raw = code.trim();
  if (raw === '' || raw.toLowerCase() === 'und' || raw.toLowerCase() === 'zxx') return null;
  const tag = ISO_639_2[raw.toLowerCase()] ?? raw;
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(tag);
    if (name && name.toLowerCase() !== tag.toLowerCase()) return name;
  } catch {
    // Not a tag Intl accepts: show the code as the file has it.
  }
  return raw;
}

const ORDER: Readonly<Record<ContainerTrackInfo['kind'], number>> = {
  text: 0,
  image: 1,
  unsupported: 2,
};

/**
 * `trackWord` is the page's word for "Track" and `locale` its language (`<html lang>`), so a
 * translated page names the languages in its own: 영어, not English.
 */
export function pickerModel(
  tracks: readonly ContainerTrackInfo[],
  trackWord = 'Track',
  locale = 'en',
): PickerModel {
  const rows = tracks
    .map((track, order) => ({ track, order }))
    .sort((a, b) => ORDER[a.track.kind] - ORDER[b.track.kind] || a.order - b.order)
    .map(({ track }): PickerRow => {
      const language = languageName(track.language, locale);
      const title = track.title?.trim() || null;
      const name =
        language && title && title.toLowerCase() !== language.toLowerCase()
          ? `${language} — ${title}`
          : (language ?? title ?? `${trackWord} ${track.index}`);
      return { track, name, format: track.codecLabel, kind: track.kind };
    });
  const textCount = tracks.filter((t) => t.kind === 'text').length;
  const imageCount = tracks.filter((t) => t.kind === 'image').length;
  return {
    rows,
    auto: tracks.length === 1 ? (tracks[0] ?? null) : null,
    textCount,
    imageCount,
  };
}

export interface PickerHandle {
  /** Put a sentence under the list (`role=status`): why a picked track could not be used. */
  note: (content: Node | null) => void;
  /** Stop accepting picks: the picked track is being read. */
  busy: (on: boolean) => void;
}

/**
 * Draws the picker into `host` and calls `onPick` with the chosen track. Returns null when the
 * page carries no picker template, which the caller treats as "take the first text track".
 */
export function mountTrackPicker(
  host: HTMLElement,
  model: PickerModel,
  onPick: (track: ContainerTrackInfo) => void,
): PickerHandle | null {
  const tpl = qs<HTMLTemplateElement>('template[data-track-picker]');
  const rowTpl = qs<HTMLTemplateElement>('template[data-track-row]');
  if (!tpl || !rowTpl) return null;
  const frag = tpl.content.cloneNode(true) as DocumentFragment;
  const count = qs<HTMLElement>('[data-slot="count"]', frag);
  if (count) count.textContent = String(model.rows.length);
  const list = qs<HTMLElement>('[data-track-list]', frag);
  const note = qs<HTMLElement>('[data-track-note]', frag);
  const buttons: HTMLButtonElement[] = [];
  for (const row of model.rows) {
    const item = rowTpl.content.cloneNode(true) as DocumentFragment;
    const set = (slot: string, text: string) => {
      const node = qs<HTMLElement>(`[data-slot="${slot}"]`, item);
      if (node) node.textContent = text;
    };
    set('name', row.name);
    set('format', row.format);
    // One marker per kind is in the row; the row's kind keeps its own.
    for (const kind of ['text', 'image', 'unsupported'] as const) {
      if (kind !== row.kind) qs(`[data-track-kind="${kind}"]`, item)?.remove();
    }
    const button = qs<HTMLButtonElement>('[data-track-pick]', item);
    if (button) {
      if (row.kind === 'unsupported') button.remove();
      else {
        button.addEventListener('click', () => onPick(row.track));
        buttons.push(button);
      }
    }
    list?.append(item);
  }
  host.replaceChildren(frag);
  return {
    note(content) {
      if (!note) return;
      if (content === null) note.replaceChildren();
      else note.replaceChildren(content);
    },
    busy(on) {
      for (const b of buttons) b.disabled = on;
    },
  };
}
